import {
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

import { findModel } from './modelPreference'
import {
  applyImportedPreference,
  frontierImportHref,
  preferenceEnvelopeForShare,
  type PreferenceImportCandidate,
  type PreferenceTransferEnvelope,
} from './preferenceTransfer'
import { ProviderIcon } from './ProviderIcon'

function PreferencePreview({
  envelope,
}: {
  envelope: PreferenceTransferEnvelope
}) {
  if (envelope.pinnedModel) {
    const model = findModel(
      envelope.pinnedModel.modelId,
      envelope.pinnedModel.provider,
    )
    return (
      <dl className="preference-preview">
        <div>
          <dt>Pinned model</dt>
          <dd>
            <span className="preference-preview__identity">
              <ProviderIcon
                provider={envelope.pinnedModel.provider}
                modelName={model?.name}
              />
              <strong>{model?.name ?? envelope.pinnedModel.modelId}</strong>
            </span>
            <code>
              {envelope.pinnedModel.provider} /{' '}
              {envelope.pinnedModel.modelId}
            </code>
          </dd>
        </div>
      </dl>
    )
  }

  return (
    <dl className="preference-preview">
      <div>
        <dt>Preferred provider</dt>
        <dd>
          <span className="preference-preview__identity">
            <ProviderIcon
              provider={envelope.pinnedProvider?.provider ?? ''}
            />
            <strong>{envelope.pinnedProvider?.provider}</strong>
          </span>
        </dd>
      </div>
    </dl>
  )
}

function PreferencePageShell({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string
  title: string
  children: ReactNode
}) {
  return (
    <main className="preference-page">
      <p className="about-kicker">{eyebrow}</p>
      <h1>{title}</h1>
      {children}
      <p className="preference-page__privacy">
        This transfer contains only one provider or model
        preference. It never includes prompts, history, account
        identifiers, or analytics identifiers.
      </p>
    </main>
  )
}

export function PreferenceSharePage() {
  const [envelope] = useState(preferenceEnvelopeForShare)

  return (
    <PreferencePageShell
      eyebrow="Preference transfer"
      title="Share your Router preference"
    >
      {envelope ? (
        <>
          <p className="preference-page__lede">
            Check the exact preference below, then continue to You
            Might Not Need Frontier. Nothing is sent until you
            follow the link.
          </p>
          <PreferencePreview envelope={envelope} />
          <a
            className="preference-transfer-action"
            href={frontierImportHref(envelope)}
          >
            Continue to You Might Not Need Frontier
          </a>
        </>
      ) : (
        <>
          <p className="preference-page__lede">
            There is no saved model or provider preference to
            share yet.
          </p>
          <a className="preference-transfer-action" href="/">
            Choose a preference
          </a>
        </>
      )}
    </PreferencePageShell>
  )
}

export function PreferenceImportPage({
  candidate,
}: {
  candidate: PreferenceImportCandidate
}) {
  const [importStatus, setImportStatus] = useState<
    'ready' | 'saved' | 'failed'
  >('ready')
  const resultRef = useRef<HTMLParagraphElement>(null)

  function confirmImport(envelope: PreferenceTransferEnvelope) {
    setImportStatus(
      applyImportedPreference(envelope) ? 'saved' : 'failed',
    )
  }

  useEffect(() => {
    if (importStatus !== 'ready') resultRef.current?.focus()
  }, [importStatus])

  return (
    <PreferencePageShell
      eyebrow="Preference transfer"
      title="Import a Frontier preference"
    >
      {candidate.status === 'valid' ? (
        <>
          <p className="preference-page__lede">
            Review the exact preference. SamRouter will save it
            only after you confirm.
          </p>
          <PreferencePreview envelope={candidate.envelope} />
          {importStatus === 'saved' ? (
            <p
              className="preference-transfer-success"
              role="status"
              tabIndex={-1}
              ref={resultRef}
            >
              Preference imported. It will appear first in model
              suggestions.
            </p>
          ) : (
            <>
              {importStatus === 'failed' ? (
                <p
                  className="preference-transfer-error"
                  role="alert"
                  tabIndex={-1}
                  ref={resultRef}
                >
                  SamRouter could not save this preference in
                  browser storage. Nothing was imported.
                </p>
              ) : null}
              <button
                className="preference-transfer-action"
                type="button"
                onClick={() => confirmImport(candidate.envelope)}
              >
                {importStatus === 'failed'
                  ? 'Try import again'
                  : 'Confirm import'}
              </button>
            </>
          )}
        </>
      ) : candidate.status === 'invalid' ? (
        <>
          <p className="preference-transfer-error" role="alert">
            This preference link cannot be imported.{' '}
            {candidate.message}
          </p>
          <a className="preference-transfer-action" href="/">
            Back to SamRouter
          </a>
        </>
      ) : (
        <>
          <p className="preference-page__lede">
            Open the import link from You Might Not Need Frontier
            to review a preference here.
          </p>
          <a className="preference-transfer-action" href="/">
            Back to SamRouter
          </a>
        </>
      )}
    </PreferencePageShell>
  )
}
